import { v4 as uuidv4 } from 'uuid'
import { getEnvConfig } from 'utils/getEnvConfig'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { IApiResponse, API_RESPONSE_TYPE } from './../../constants'
import ApiService from './ApiService'
import {
  apiRequestInterceptor,
  apiResponseErrorInterceptor,
  businessIdInterceptor
} from './apiInterceptor'

/**
 * Device registration client — mirrors Android's device flow so the backend
 * knows which web browsers to push FCM signals to.
 *   - PUT  app/v2.0/devices           → create/update this device (carries fcm_token)
 *   - POST app/v1.0/devices/{id}/link → link the device to the active business
 *
 * A stable per-browser deviceId is persisted in AuthStorage and reused across
 * logins (the device outlives any one session).
 */

const headers = {
  'content-type': 'application/json',
  accept: 'application/json, text/plain, */*'
}

const endpoint = `${getEnvConfig('WEB_BASE_URL')}/app`

const CREATE_OR_UPDATE_DEVICE = '/v2.0/devices'
const linkDevicePath = (deviceId: string) => `/v1.0/devices/${deviceId}/link`

const api = new ApiService(endpoint, headers)
api.requestInterceptor(apiRequestInterceptor, (error: any) => console.error(error))
api.requestInterceptor(businessIdInterceptor, (error: any) => console.error(error))
api.responseInterceptor((res: any) => res, apiResponseErrorInterceptor)

/** Get (or lazily create + persist) this browser's stable device id. */
export const getOrCreateWebDeviceId = (): string => {
  let deviceId = AuthStorageInstance.getDeviceId()
  if (!deviceId) {
    deviceId = uuidv4()
    AuthStorageInstance.setDeviceId(deviceId)
  }
  return deviceId
}

interface IApiDevice {
  name: string
  fcm_token: string
  os: string
  os_version: string
  make: string
  model: string
  create_time: number
  update_time: number
  version_code: number
  api_level: number
}

/** Best-effort browser descriptors for the device record (non-critical fields). */
const buildDeviceDescriptors = (): Pick<IApiDevice, 'os' | 'os_version' | 'make' | 'model'> => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const platform = typeof navigator !== 'undefined' ? (navigator as any).platform || '' : ''
  const vendor = typeof navigator !== 'undefined' ? (navigator as any).vendor || '' : ''
  return {
    os: 'web',
    os_version: ua,
    make: vendor || 'web',
    model: platform || 'web'
  }
}

/**
 * Create/update this device on the backend with its current FCM token.
 * Returns the deviceId used, or null on failure.
 */
export const createOrUpdateDevice = async (fcmToken: string): Promise<string | null> => {
  const deviceId = getOrCreateWebDeviceId()
  const now = Math.floor(Date.now() / 1000)
  const device: IApiDevice = {
    name: deviceId,
    fcm_token: fcmToken,
    create_time: now,
    update_time: now,
    version_code: 0,
    api_level: 0,
    ...buildDeviceDescriptors()
  }
  const res: IApiResponse = await api.putRequest(CREATE_OR_UPDATE_DEVICE, { device })
  return res.type === API_RESPONSE_TYPE.SUCCESS ? deviceId : null
}

/**
 * Link this device to a business so the backend fans out that business's push
 * signals to it. `businessId` is also carried by the businessIdInterceptor, but
 * we pass it explicitly to avoid any race with the just-selected business.
 */
export const linkDeviceWithBusiness = async (
  deviceId: string,
  businessId: string
): Promise<boolean> => {
  const res: IApiResponse = await api.postRequest(linkDevicePath(deviceId), undefined, {
    'OKC-Business-Id': businessId
  })
  return res.type === API_RESPONSE_TYPE.SUCCESS
}
